import React, { MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { NextSeo } from "next-seo";
import { REFRAME_LOCATION_LABELS } from "domains/assets/constants/reframe";
import { useAssetReframe } from "domains/assets/hooks/useAssetReframe";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { FmImageListActionsProps } from "domains/file-manager/components/FileManagerImage/List/Actions";
import CopyPromptButton from "domains/file-manager/components/FileManagerImage/List/CopyPromptButton";
import MoreTooltip from "domains/file-manager/components/FileManagerImage/List/MoreTooltip";
import ReuseButton from "domains/file-manager/components/FileManagerImage/List/ReuseButton";
import { FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import {
  SectionsProvider,
  useSectionsContext,
} from "domains/inference/contexts/SectionsProvider";
import useImportParamsReframe from "domains/inference/hooks/useImportParamsReframe";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import ReframeParams from "domains/reframe/components/ReframeParams";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

export default function ImageReframePage() {
  return (
    <>
      <NextSeo title="Expand Image" />

      <SectionsProvider defaultCollapsedSections={["negativePrompt"]}>
        <JobSessionProvider
          forcedFilterTypeOptions={[
            ...FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS,
          ]}
        >
          <ImageReframe />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

function ImageReframe() {
  const { infoToast } = useScenarioToast();
  const { openSection } = useSectionsContext();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    form,
    setForm,
    setValue,
    cuCost,
    isCuLoading,
    errors,
    isDisabled,
    handleReframeJob,
    isReframeLoading,
  } = useAssetReframe();

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
    dimensions: {
      width: form.targetWidth || 1_024,
      height: form.targetHeight || 1_024,
    },
  });

  // ----------------------------------

  const handleGenerate = useCallback(async () => {
    if (!form.asset) {
      return;
    }

    infoToast({
      title: "Expanding image",
    });

    const job = await handleReframeJob({
      asset: form.asset,
      trackingExtraParams: {
        origin: "expand page",
      },
    });
    if (job) addJob(job);
  }, [infoToast, handleReframeJob, form, addJob]);

  const handleMoreClick = useCallback(
    (key: string, value: any) => {
      switch (key) {
        case "inputLocation":
        case "numInferenceSteps":
          setValue(key, value);
          openSection("settings");
          break;
      }

      infoToast({
        title: "Settings updated",
      });
    },
    [infoToast, openSection, setValue]
  );

  const handleCopyPromptClick = useCallback<
    ListActionsProps["onCopyPromptClick"]
  >(
    ({ prompt }) => {
      openSection("prompt");
      setValue("prompt", prompt);

      infoToast({
        title: "Settings updated",
      });
    },
    [openSection, setValue, infoToast]
  );

  const handleReuseSeedClick = useCallback<
    CardActionsCustomProps["onReuseSeedClick"]
  >(
    (seed) => {
      setValue("seed", seed);
      infoToast({
        title: "Settings updated",
      });
    },
    [setValue, infoToast]
  );

  const handleRefineClick = useCallback<
    CardActionsCustomProps["onRefineClick"]
  >(
    async (asset) => {
      await setValue("asset", asset);
      infoToast({
        title: "Settings updated",
      });
    },
    [setValue, infoToast]
  );

  // ----------------------------------

  useImportParamsReframe({
    setValue: setValue,
    setForm: setForm,
  });

  // ----------------------------------

  const isRefFilled = !!form.asset;
  const listActionsProps = useMemo(
    () => ({
      isRefFilled,
      onCopyPromptClick: handleCopyPromptClick,
      onMoreClick: handleMoreClick,
    }),
    [isRefFilled, handleCopyPromptClick, handleMoreClick]
  );

  const cardActionsProps = useMemo(
    () => ({
      onReuseSeedClick: handleReuseSeedClick,
      onRefineClick: handleRefineClick,
    }),
    [handleReuseSeedClick, handleRefineClick]
  );

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={handleGenerate}>
      <GeneratorSidebar>
        <SidebarTitle
          title="Expand"
          videoUrl="https://www.youtube.com/embed/rVLKjjwBIhI?si=UWzfNo4JB91kuPVT"
          videoTooltip="Watch a quick video to learn about Scenario Expand"
        />
        <Divider />

        <ReframeParams
          isAssetZoom={false}
          form={form}
          onFormChange={setValue}
          onGenerate={handleGenerate}
          isGenerating={isReframeLoading}
          errors={errors}
          cuCost={cuCost}
          isDisabled={isDisabled}
          isCuLoading={isCuLoading}
        />
        <Divider />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <FileManagerImage
          scrollRef={scrollRef}
          files={
            !files?.length && !jobs?.length && !isLoading ? placeholders : files
          }
          jobs={jobs}
          isSelectable
          isLoading={isLoading}
          ListActionsComponent={ListActions}
          listActionsProps={listActionsProps}
          CardActionsComponent={CardActions}
          cardActionsProps={cardActionsProps}
          {...fileManagerProps}
          styleProps={FM_HEADER_TOP_0}
        />
      </GeneratorContent>
    </GeneratorContainer>
  );
}

// ------------------------------------

export type CardActionsCustomProps = {
  onRefineClick: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
  onReuseSeedClick: (seed: number) => void;
};

export type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({
  asset,
  top,
  bottom,
  onRefineClick,
  onReuseSeedClick,
}: CardActionsProps) {
  const seed = asset?.metadata.seed;
  const canReuseImage = !["vectorization"].includes(asset?.metadata.type ?? "");

  const handleImageClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedImage);
      if (asset) {
        onRefineClick(asset);
      }
    },
    [onRefineClick, asset]
  );

  const handleSeedClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedSeed);
      if (seed) {
        onReuseSeedClick(parseInt(seed));
      }
    },
    [onReuseSeedClick, seed]
  );

  if (!(seed || canReuseImage)) {
    return null;
  }

  return (
    <>
      {top}

      {canReuseImage && (
        <MenuItem
          w="170px"
          iconId="Nav/Image/Outline"
          iconH="12px"
          onClick={handleImageClick}
          text="Image"
        />
      )}

      {!!seed && (
        <MenuItem
          w="170px"
          iconId="Ui/Key"
          iconH="14px"
          onClick={handleSeedClick}
          text="Seed"
        />
      )}

      {bottom}
    </>
  );
}

// ------------------------------------

export type ListActionsCustomProps = {
  isRefFilled: boolean;
  onCopyPromptClick: (arg: { prompt: string; negativePrompt?: string }) => void;
  onMoreClick: (key: string, value: any) => void;
};

export type ListActionsProps = FmImageListActionsProps<ListActionsCustomProps>;

function ListActions({
  job,
  firstAsset,
  isRefFilled,
  onCopyPromptClick,
  onMoreClick,
}: ListActionsProps) {
  const { isReader } = useTeamContext();
  const jobInput = job?.metadata.input as any;
  const inputDimensions = `${jobInput?.inputWidth} x ${jobInput?.inputHeight} px`;
  const inputLocation = jobInput?.inputLocation;
  const numInferenceSteps = jobInput?.numInferenceSteps;
  const isActionDisplayed = job?.jobType === "reframe" && !isReader;

  const moreOptions = useMemo(() => {
    if (!isActionDisplayed) {
      return [];
    }
    return _.compact([
      inputDimensions !== undefined && {
        id: "inputDimensions",
        label: "Dimension",
        value: inputDimensions,
      },
      inputLocation !== undefined && {
        id: "inputLocation",
        label: "Location",
        value: _.get(REFRAME_LOCATION_LABELS, [inputLocation]) ?? inputLocation,
        onClick: isRefFilled
          ? () => onMoreClick("inputLocation", inputLocation)
          : undefined,
      },
      numInferenceSteps !== undefined && {
        id: "numInferenceSteps",
        label: "Inference Steps",
        value: numInferenceSteps,
        onClick: () => onMoreClick("numInferenceSteps", numInferenceSteps),
      },
    ]);
  }, [
    inputDimensions,
    inputLocation,
    numInferenceSteps,
    isRefFilled,
    onMoreClick,
    isActionDisplayed,
  ]);

  if (isReader) {
    return null;
  }

  return (
    <>
      {isActionDisplayed && <ReuseButton job={job} firstAsset={firstAsset} />}
      <CopyPromptButton
        job={job}
        firstAsset={firstAsset}
        onClick={onCopyPromptClick}
      />
      {isActionDisplayed && <MoreTooltip options={moreOptions} />}
    </>
  );
}
